import { partyTaxStatus, type PartyTax } from '../../lib/partyTaxStatus'

// The EORI of the company under an Importer / Consignee box. Always says something: the value, or WHY there is none and what to do.
// EORI missing = customs cannot be declared for this importer (the pre-submit check stops it): orange block. A value the check refuses: red.
// EORI ONLY: the VAT number does not affect the declaration and has its own checks on the invoicing side, so this page says nothing about it.
export default function PartyTaxIds({ tax, companyName, canEdit }: { tax: PartyTax; companyName: string | null; canEdit: boolean }) {
  if (tax.error && !tax.loaded) return <p className="mt-1 text-xs text-red-600 dark:text-red-400">Could not load the EORI: {tax.error}</p>
  const s = partyTaxStatus(tax, canEdit)
  if (!s) return <p className="mt-1 text-xs text-gray-400">Loading EORI…</p>
  const who = companyName || 'This company'
  const how = canEdit
    ? 'Click the arrow ↗ on the right of the company box to add it.'
    : 'Your role cannot edit companies: ask Accounts, Sales or Operations to add it.'
  const eori = (tax.eori ?? '').trim()

  return (
    <div className="mt-2" data-testid="party-tax-ids">
      {s.eori === 'missing' ? (
        <div className="rounded-md border-2 border-orange-400 bg-orange-50 dark:bg-orange-900/20 px-3 py-2 text-sm text-orange-900 dark:text-orange-200" role="alert">
          <span className="font-semibold">⚠ No EORI for {who}.</span>{' '}
          Without it the customs declaration for this importer is refused: the pre-submit check stops it ("The importer has no EORI on its customer record"). {how}
        </div>
      ) : s.eori === 'bad_format' ? (
        <div className="rounded-md border-2 border-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-800 dark:text-red-200" role="alert">
          <span className="font-semibold">⛔ EORI “{eori}” is not a valid EORI.</span>{' '}
          It must be two letters followed by 5-15 letters or digits. The pre-submit check refuses the declaration with this value. {how}
        </div>
      ) : (
        <p className="text-sm text-gray-800 dark:text-gray-200">
          <span className="text-gray-500 dark:text-gray-400">EORI</span>{' '}
          <span className="font-mono font-medium">{eori.toUpperCase()}</span>
          {s.eori === 'ok_check_uk' && <span className="ml-2 text-xs text-amber-700 dark:text-amber-400">Does not match the usual UK shape (GB + 9-12 digit VAT no. + 000) — double-check.</span>}
        </p>
      )}
    </div>
  )
}
